import type { WalkthroughStep } from '../../store/walkthroughStore';
import type { CapturedUiElement } from './capturePageMap';
import type { TourRegion } from './pageRegions';
import type { TourAmbiente } from './tourEnv';

const API = import.meta.env.VITE_API_URL || '/api/v1';

export type TourEnsureResult = {
  ok: boolean;
  cached?: boolean;
  fonte?: string;
  pageResenha?: string | null;
  passos: WalkthroughStep[];
  error?: string;
};

export type EnsureTourPayload = {
  perfil: string;
  urlPath: string;
  cargo?: string | null;
  pageTitle?: string;
  regions?: TourRegion[];
  elements?: CapturedUiElement[];
  contentHash?: string;
  screenshotBase64?: string;
  pageHtml?: string;
  force?: boolean;
  ambiente?: TourAmbiente;
};

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('ac_token') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function fetchCachedTour(
  perfil: string,
  urlPath: string,
  ambiente: TourAmbiente = 'APP',
): Promise<TourEnsureResult | null> {
  try {
    const q = new URLSearchParams({ perfil, url: urlPath, ambiente });
    const res = await fetch(`${API}/training/tours?${q}`, {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.ok || !Array.isArray(data.passos) || data.passos.length === 0) {
      return null;
    }
    return {
      ok: true,
      cached: true,
      fonte: data.fonte,
      pageResenha: data.pageResenha ?? null,
      passos: data.passos as WalkthroughStep[],
    };
  } catch {
    return null;
  }
}

export async function ensureTour(payload: EnsureTourPayload): Promise<TourEnsureResult> {
  try {
    const res = await fetch(`${API}/training/tours/ensure`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ ambiente: 'APP', ...payload }),
    });
    if (!res.ok) {
      return { ok: false, passos: [], error: `HTTP ${res.status}` };
    }
    const data = await res.json();
    if (!data?.ok || !Array.isArray(data.passos)) {
      return { ok: false, passos: [], error: 'Resposta inválida' };
    }
    return {
      ok: true,
      cached: !!data.cached,
      fonte: data.fonte,
      pageResenha: data.pageResenha ?? null,
      passos: data.passos as WalkthroughStep[],
    };
  } catch (err: any) {
    return { ok: false, passos: [], error: err?.message || 'Falha na rede' };
  }
}

export async function regenerateTour(
  payload: EnsureTourPayload,
): Promise<TourEnsureResult> {
  try {
    const res = await fetch(`${API}/training/tours/regenerate`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ ambiente: 'APP', ...payload, force: true }),
    });
    if (!res.ok) {
      return { ok: false, passos: [], error: `HTTP ${res.status}` };
    }
    const data = await res.json();
    if (!data?.ok || !Array.isArray(data.passos)) {
      return { ok: false, passos: [], error: 'Resposta inválida' };
    }
    return {
      ok: true,
      cached: false,
      fonte: data.fonte,
      pageResenha: data.pageResenha ?? null,
      passos: data.passos as WalkthroughStep[],
    };
  } catch (err: any) {
    return { ok: false, passos: [], error: err?.message || 'Falha na rede' };
  }
}
